/// <amd-module name="VSS/Platform/Util/DOM" />

/**
 * Determine if a parent element contains a given element. This is similar to the DOM contains
 * method except it will support detached DOM trees. A detached DOM tree is where a element
 * logically is within the container but not in the actual DOM. The primary example is a popup
 * menu. The menu exists logically within the element the menu is associated with though it
 * is not contained within the DOM structure.
 *
 * NOTE: This uses the aria-owns attribute to determine when a detached DOM tree exists.
 *
 * @param containerElement The parent element that is being checked for the child element.
 * @param containedElement The child element that is being looked up in the DOM tree.
 */
export function contains(containerElement: Element, containedElement: Element): boolean {
    // Determine whether or not this components DOM hierarchy contains the specified element.
    if (containerElement.contains(containedElement)) {
        return true;
    }

    // Check if the containerElement defines an aria-owns before scanning the sub-tree.
    const ariaOwns = containerElement.getAttribute("aria-owns");
    if (ariaOwns) {
        if (detachedContains(ariaOwns, containedElement)) {
            return true;
        }
    }

    // If the containerElement and its optional aria-owns don't contain the element scan the container for child aria-owns.
    const ariaOwnsElements = containerElement.querySelectorAll("[aria-owns]");
    for (let index = 0; index < ariaOwnsElements.length; index++) {
        const childElement = ariaOwnsElements[index];

        if (detachedContains(childElement.getAttribute("aria-owns")!, containedElement)) {
            return true;
        }
    }

    return false;
}

function detachedContains(id: string, containedElement: Element): boolean {
    // Find the detached element in the DOM if it exists.
    const detachedElement = document.querySelector("[id='" + id + "']");
    if (detachedElement) {
        if (contains(detachedElement, containedElement)) {
            return true;
        }
    }

    // Check for another detached element within this detached element.
    return false;
}
